import { FilterTypes } from '@carboledger/ui';
import { COUNTRY_CODES } from '@/config/countries.constants';
import { buildCountryOptions } from '@/lib/utils';
import { DATE_RANGE_PRESETS } from '@/lib/mappers';
import {
  buildAllocationPreferenceOptions,
  buildFeedstockCategoryOptions,
} from '@/config/formOptions';
import { buildRawMaterialTypeOptions } from '@/config/rawMaterialTypes.constants';
import {
  buildContractStatusFilterOptions,
  SHIPMENT_STATUS_FILTER_LABEL_KEYS,
  OUTGOING_SHIPMENT_STATUS_LABEL_KEYS,
} from '@/config/statuses';

// Filter panels for every contract and shipment table; each field is one the server accepts.

export const CONTRACT_FILTERS = Object.freeze({
  status: 'status',
  materialId: 'materialId',
  supplierAccountId: 'supplierAccountId',
  deliverBy: 'deliverBy',
  countryOfOrigin: 'countryOfOrigin',
  rawMaterialType: 'rawMaterialType',
  feedstockCategory: 'feedstockCategory',
  preferredAllocation: 'preferredAllocation',
});

export const SHIPMENT_FILTERS = Object.freeze({
  status: 'status',
  dateOfDispatch: 'dateOfDispatch',
  dateOfDischarge: 'dateOfDischarge',
  countryOfOrigin: 'countryOfOrigin',
  rawMaterialType: 'rawMaterialType',
  feedstockCategory: 'feedstockCategory',
  allocationPreference: 'allocationPreference',
  materialId: 'materialId',
  supplierId: 'supplierId',
});

const dateRange = (t, field, label) => ({
  field,
  label,
  type: FilterTypes.dateRange,
  presets: [
    { value: DATE_RANGE_PRESETS.last7, label: t('FILTER_LAST_7_DAYS') },
    { value: DATE_RANGE_PRESETS.last30, label: t('FILTER_LAST_30_DAYS') },
    { value: DATE_RANGE_PRESETS.last60, label: t('FILTER_LAST_60_DAYS') },
  ],
  rangeLabel: t('FILTER_SELECT_RANGE'),
});

// Country and raw-material lists are too long to draw as checkboxes.
const searchableSelect = (t, field, label, options, searchPlaceholder) => ({
  field,
  label,
  type: FilterTypes.select,
  placeholder: t('COMMON_SELECT'),
  searchPlaceholder,
  noResultsMessage: t('COMMON_NO_MATCHES'),
  options,
});

const countryFilter = (t, field) =>
  searchableSelect(
    t,
    field,
    t('FIELD_COUNTRY_OF_ORIGIN'),
    buildCountryOptions(COUNTRY_CODES),
    t('COMMON_SEARCH_COUNTRY'),
  );

const rawMaterialFilter = (t, field) =>
  searchableSelect(
    t,
    field,
    t('FIELD_RAW_MATERIAL_TYPE'),
    buildRawMaterialTypeOptions(),
    t('COMMON_SEARCH_RAW_MATERIAL'),
  );

// A list's own *FilterOptions query fills these; a caller that passes none gets no filter.
export const materialFilter = (t, field, options) =>
  options
    ? [searchableSelect(t, field, t('FIELD_MATERIAL_NAME'), options, t('COMMON_SEARCH_MATERIAL'))]
    : [];

export const supplierFilter = (t, field, options) =>
  options
    ? [searchableSelect(t, field, t('FIELD_SUPPLIER_NAME'), options, t('COMMON_SEARCH_SUPPLIER'))]
    : [];

export const customerFilter = (t, field, options) =>
  options
    ? [searchableSelect(t, field, t('FIELD_CUSTOMER_NAME'), options, t('COMMON_SEARCH_CUSTOMER'))]
    : [];

const feedstockFilter = (t, field) => ({
  field,
  label: t('FIELD_FEEDSTOCK_CATEGORY'),
  type: FilterTypes.checkbox,
  options: buildFeedstockCategoryOptions(t),
});

const allocationFilter = (t, field) => ({
  field,
  label: t('FIELD_ALLOCATION_PREFERENCE'),
  type: FilterTypes.checkbox,
  options: buildAllocationPreferenceOptions(t),
});

// TODO: discrepancy count filter once the server exposes one.
export const buildContractFilterDefinitions = (t, { materialOptions, supplierOptions } = {}) => [
  {
    field: CONTRACT_FILTERS.status,
    label: t('FIELD_STATUS'),
    type: FilterTypes.checkbox,
    options: buildContractStatusFilterOptions(t),
  },
  ...materialFilter(t, CONTRACT_FILTERS.materialId, materialOptions),
  ...supplierFilter(t, CONTRACT_FILTERS.supplierAccountId, supplierOptions),
  dateRange(t, CONTRACT_FILTERS.deliverBy, t('FIELD_DELIVER_BY')),
  countryFilter(t, CONTRACT_FILTERS.countryOfOrigin),
  rawMaterialFilter(t, CONTRACT_FILTERS.rawMaterialType),
  feedstockFilter(t, CONTRACT_FILTERS.feedstockCategory),
  allocationFilter(t, CONTRACT_FILTERS.preferredAllocation),
];

const buildShipmentStatusOptions = (t) =>
  Object.entries(SHIPMENT_STATUS_FILTER_LABEL_KEYS).map(([value, key]) => ({
    value,
    label: t(key),
  }));

export const buildIncomingShipmentFilterDefinitions = (
  t,
  { materialOptions, supplierOptions } = {},
) => [
  {
    field: SHIPMENT_FILTERS.status,
    label: t('FIELD_STATUS'),
    type: FilterTypes.checkbox,
    options: buildShipmentStatusOptions(t),
  },
  ...materialFilter(t, SHIPMENT_FILTERS.materialId, materialOptions),
  ...supplierFilter(t, SHIPMENT_FILTERS.supplierId, supplierOptions),
  dateRange(t, SHIPMENT_FILTERS.dateOfDispatch, t('FIELD_DATE_OF_DISPATCH')),
  dateRange(t, SHIPMENT_FILTERS.dateOfDischarge, t('FIELD_DATE_OF_DISCHARGE')),
  countryFilter(t, SHIPMENT_FILTERS.countryOfOrigin),
  rawMaterialFilter(t, SHIPMENT_FILTERS.rawMaterialType),
  feedstockFilter(t, SHIPMENT_FILTERS.feedstockCategory),
  allocationFilter(t, SHIPMENT_FILTERS.allocationPreference),
];

// The Fulfill order flow's two eligibility pickers — Select outgoing shipment and Select inbound.
export const FULFILL_PICKER_FILTERS = Object.freeze({
  status: 'status',
  discrepancies: 'discrepancies',
});

const buildFulfillPickerStatusOptions = (t) =>
  Object.entries(OUTGOING_SHIPMENT_STATUS_LABEL_KEYS).map(([value, key]) => ({
    value,
    label: t(key),
  }));

const DISCREPANCY_FILTER_VALUES = Object.freeze(['NONE', '1', '2', '3', '3+']);

const discrepancyFilterLabel = (t, value) => {
  if (value === 'NONE') return t('DISCREPANCIES_NONE');
  if (value === '1') return t('DISCREPANCIES_FOUND_ONE');
  if (value === '3+') return t('DISCREPANCIES_CAPPED', { cap: 3 });
  return t('DISCREPANCIES_FOUND', { count: Number(value) });
};

const buildDiscrepancyFilterOptions = (t) =>
  DISCREPANCY_FILTER_VALUES.map((value) => ({ value, label: discrepancyFilterLabel(t, value) }));

export const buildFulfillPickerFilterDefinitions = (t) => [
  {
    field: FULFILL_PICKER_FILTERS.status,
    label: t('FIELD_STATUS'),
    type: FilterTypes.checkbox,
    options: buildFulfillPickerStatusOptions(t),
  },
  {
    field: FULFILL_PICKER_FILTERS.discrepancies,
    label: t('FIELD_DISCREPANCIES'),
    type: FilterTypes.checkbox,
    options: buildDiscrepancyFilterOptions(t),
  },
];
